import Head from 'next/head'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import type { CareersDetailProps } from './types'
import styles from './CareersDetail.module.scss'
import { generateJobPostingStructuredData } from 'utils/structuredData'
import { routes } from 'utils/routes'
import { Heading, Header } from '@boltenergy-be/design-system'
import Link from 'components/link/Link'

const CareersDetail = ({ jobOffer }: CareersDetailProps) => {
  // Next router
  const router = useRouter()
  const { asPath, locale } = router

  // i18n
  const { t } = useTranslation(['common', 'careers'])

  // Destructure job offer
  const { description, mailTo, title } = jobOffer.attributes

  return (
    <>
      {/* SEO */}
      <Head>
        <script type="application/ld+json" key="job-posting-jsonld" dangerouslySetInnerHTML={generateJobPostingStructuredData(jobOffer)} />
        <link rel="canonical" href={`https://www.boltenergie.be/${locale}${asPath}`} />
      </Head>

      <main className={styles['career-detail']}>
        {/* HERO */}
        <Header docked title={title} color="accent-vibrant">
          <strong className="h5 serif">{parse(t('careers:vacancy', 'Vacature'))}</strong>
        </Header>

        {/* CAREER CONTENT */}
        <section className={classNames('section', styles['career-content'])}>
          <div className={classNames('container', styles.container)}>
            <button onClick={() => router.push(routes(locale).careers)} className={styles['back-button']}>
              &laquo; {t('back', 'Ga terug')}
            </button>
            <Heading as="h2" variant="h3">
              {title}
            </Heading>
            <div>{parse(description)}</div>
            {mailTo && (
              <>
                <Link
                  as="a"
                  representation="button"
                  className="mt-300"
                  href={`mailto:${mailTo}?subject=${t('careers:vacancy', 'Vacature')} ${title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('careers:applyNow', 'Solliciteer nu')}
                </Link>
                <p>{parse(t('careers:emailTo', { email: mailTo }))}</p>
              </>
            )}
          </div>
        </section>
      </main>
    </>
  )
}

export default CareersDetail
